/**
 * Mention markup helpers.
 *
 * The backend extracts @mentions from comment content using the literal
 * pattern `@[user-id]` (see `app/comments/mentions.py` in the backend,
 * `MENTION_PATTERN`). Ids are normally UUIDs, but the seeded admin user has
 * the literal id "admin", so any id-safe token is accepted. This file is
 * the frontend's single source of truth for that exact format — anything
 * that writes or reads comment content should go through here rather than
 * re-deriving the pattern.
 */

export const MENTION_REGEX = /@\[([A-Za-z0-9_-]{1,36})\]/g

export interface MentionTextSegment {
  type: 'text'
  value: string
}

export interface MentionUserSegment {
  type: 'mention'
  userId: string
}

export type MentionSegment = MentionTextSegment | MentionUserSegment

/**
 * Splits raw comment content into ordered plain-text and mention segments,
 * for rendering. Does not resolve mention ids to names — callers supply that.
 */
export function splitMentionSegments(content: string): MentionSegment[] {
  const segments: MentionSegment[] = []
  const regex = new RegExp(MENTION_REGEX)
  let lastIndex = 0
  let match: RegExpExecArray | null

  while ((match = regex.exec(content)) !== null) {
    if (match.index > lastIndex) {
      segments.push({
        type: 'text',
        value: content.slice(lastIndex, match.index),
      })
    }
    segments.push({ type: 'mention', userId: match[1] })
    lastIndex = match.index + match[0].length
  }

  if (lastIndex < content.length) {
    segments.push({ type: 'text', value: content.slice(lastIndex) })
  }

  return segments
}

/** Returns the distinct set of mentioned user ids in raw comment content. */
export function extractMentionIds(content: string): string[] {
  const ids = new Set<string>()
  const regex = new RegExp(MENTION_REGEX)
  let match: RegExpExecArray | null
  while ((match = regex.exec(content)) !== null) {
    ids.add(match[1])
  }
  return [...ids]
}
